import React, { useLayoutEffect, useRef, useState } from "react";
import "./styles/ExpandableText.css";

interface ExpandableTextProps {
  text: string;
  /** Class applied to the text element so callers keep their own typography. */
  className?: string;
  /** Number of lines shown while collapsed. */
  lines?: number;
}

const ELLIPSIS = "…";

const ExpandableText: React.FC<ExpandableTextProps> = ({ text, className = "", lines = 4 }) => {
  const textRef = useRef<HTMLParagraphElement>(null);
  const measureRef = useRef<HTMLParagraphElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [width, setWidth] = useState(0);
  // null = the full text fits in `lines`, so no toggle is needed.
  const [truncated, setTruncated] = useState<string | null>(null);

  // Track the rendered width only; height changes with truncation and would loop.
  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el) return;
    setWidth(el.clientWidth);
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => setWidth(el.clientWidth));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Find the longest prefix such that "prefix… See more" still fits in `lines`,
  // measured in a hidden clone so the toggle sits right after the cut-off text.
  useLayoutEffect(() => {
    const m = measureRef.current;
    if (!m || !width) return;
    m.style.width = `${width}px`;
    const style = getComputedStyle(m);
    const lineHeight = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.2;
    const limit = lineHeight * lines + 1;

    const fits = (length: number, withToggle: boolean) => {
      m.textContent = "";
      if (!withToggle) {
        m.append(text);
      } else {
        const toggle = document.createElement("span");
        toggle.className = "expandable-text__toggle";
        toggle.textContent = "See more";
        m.append(text.slice(0, length).trimEnd() + ELLIPSIS + " ", toggle);
      }
      return m.scrollHeight <= limit;
    };

    if (fits(text.length, false)) {
      setTruncated(null);
      return;
    }
    let lo = 0;
    let hi = text.length;
    while (lo < hi) {
      const mid = Math.ceil((lo + hi) / 2);
      if (fits(mid, true)) lo = mid;
      else hi = mid - 1;
    }
    setTruncated(text.slice(0, lo).trimEnd() + ELLIPSIS);
  }, [text, lines, width]);

  const isCollapsible = truncated !== null;

  return (
    <div className="expandable-text-wrap">
      <p ref={textRef} className={`${className} expandable-text`}>
        {isCollapsible && !expanded ? truncated : text}
        {isCollapsible && (
          <>
            {" "}
            <button
              type="button"
              className="expandable-text__toggle"
              aria-expanded={expanded}
              onClick={() => setExpanded((prev) => !prev)}
            >
              {expanded ? "See less" : "See more"}
            </button>
          </>
        )}
      </p>
      <p ref={measureRef} aria-hidden="true" className={`${className} expandable-text expandable-text__measure`} />
    </div>
  );
};

export default ExpandableText;
